import { Bloodwork, ImportedExtra } from './Bloodwork';
import { CustomMarker, CustomMarkerDefinition } from './CustomMarker';
import {
  LAB_PANELS,
  LabPanel,
  LabPanelMarker,
  markersFromPanel,
  panelSectionName,
} from './LabPanelCatalog';
import { isShippedMarkerCharted } from '../utils/shippedMarkerCharted';

/**
 * What to offer a practitioner about the rows a lab delivered and nothing held.
 *
 * `Bloodwork.importedExtras` is written by the labResults lambda for exactly
 * one reason: a delivered test has nobody standing in front of the report when
 * it lands, so a row with no home has to wait somewhere until there is one.
 * This module is the "somewhere there is one" half.
 *
 * THE SPLIT IS THE WHOLE POINT, and it is a split between what we can finish
 * and what we cannot:
 *
 *   - A leftover whose name is already in the prebuilt catalog can be added for
 *     the practitioner outright. The catalog row carries units, chart type,
 *     precision, bounds and a LOINC, all mined from real reports, so what gets
 *     added is a finished marker rather than a stub.
 *
 *   - A leftover the catalog does not know can only be named. A definition
 *     needs units and a range, a stored row carries a name and a value, and
 *     inventing the rest would put a number on a chart that no lab printed and
 *     no practitioner chose. Naming it and linking to My Markers is the honest
 *     answer, and it is still the answer the practitioner needs, because an
 *     unnamed leftover is indistinguishable from a row the report never had.
 *
 * Matched on `CustomMarker.keyFor`, which is what the library's own duplicate
 * check uses, what `markersFromPanel` skips on, and what `fillRowsFromImport`
 * fills on. Using anything else here would let this module offer a marker the
 * library would then refuse to save.
 */

/** Every marker in the prebuilt catalog, with the panel it came from. */
function catalogMarkers(): { panel: LabPanel; marker: LabPanelMarker }[] {
  const all: { panel: LabPanel; marker: LabPanelMarker }[] = [];
  LAB_PANELS.forEach(panel => {
    (panel.markers || []).forEach(marker => all.push({ panel, marker }));
  });
  return all;
}

/**
 * The catalog marker a delivered row's name resolves to, if any.
 *
 * Aliases are tried after the printed name, which is the order the entry form
 * and the lambda both use: the printed name is what the practitioner is looking
 * at, so when both resolve it should win.
 *
 * First match wins. Two panels can legitimately carry the same marker (a CBC
 * extra printed inside a differential panel, say), and they carry the same
 * definition, so which one answers does not change what gets added.
 */
export function catalogMarkerFor(
  row: ImportedExtra,
): { panel: LabPanel; marker: LabPanelMarker } | undefined {
  const candidates = [row.name, ...(row.aliases || [])]
    .map(name => CustomMarker.keyFor(name || ''))
    .filter(key => !!key);
  if (!candidates.length) {
    return undefined;
  }
  const all = catalogMarkers();
  for (const key of candidates) {
    const hit = all.find(entry => CustomMarker.keyFor(entry.marker.name) === key);
    if (hit) {
      return hit;
    }
  }
  return undefined;
}

export interface ImportedExtrasOffer {
  /** Rows we can add as finished catalog markers, newest-report order kept. */
  addable: { row: ImportedExtra; panel: LabPanel; marker: LabPanelMarker }[];
  /** Rows only nameable: no catalog entry, so no units and no range. */
  nameable: ImportedExtra[];
  /**
   * Rows whose marker the library ALREADY holds, and which are therefore
   * fillable right now with nothing to set up first.
   *
   * These exist because a delivery arrives before a definition does. The lab
   * sent the row, nothing held it, it was stored; the practitioner defined the
   * marker afterwards, by hand or from a panel, and nothing went back to
   * collect the value. So the marker is in their library, the number is on the
   * test, and the chart shows neither.
   *
   * The first version of this interface called these `alreadyDefined` and left
   * them out of the notice on the reasoning that there was nothing to tell the
   * practitioner. That was backwards: this is the one group that needs no
   * decision at all.
   */
  fillable: ImportedExtra[];
  /**
   * Rows the catalog knows that LabSmarts has SINCE shipped a field for, in an
   * environment where that field is drawn.
   *
   * Adding these would be the one thing this notice must not do. The catalog
   * entry and the shipped field name the same marker, so the practitioner
   * would end up holding two of it, free to disagree, which is the trap
   * NMR_LIPOPROFILE_PANEL documents and the catalog's own test guards.
   *
   * They are still listed, because on THIS test they are genuinely not
   * charted: the row was stored as a leftover before the field existed, and
   * nothing goes back over old deliveries. What changes is the answer. The fix
   * is not My Markers, it is another pass at the report, so the notice says
   * that and offers no button.
   *
   * Environment-dependent on purpose. In prod both gated families are held and
   * the catalog panel is still the live route, so this group is empty there
   * and the rows are offered as `addable`, exactly as before.
   */
  natively: { row: ImportedExtra; attribute: string }[];
}

/**
 * Split a delivered test's leftovers into what can be added, what can only be
 * named, and what the library already covers.
 *
 * `library` is the practitioner's own marker definitions. A row it already
 * holds is deliberately dropped from both offers rather than listed as done: a
 * notice that lists work nobody has to do is a notice people learn to close.
 */
export function offerFromImportedExtras(
  extras: ImportedExtra[] | undefined,
  library: { name?: string }[] | undefined,
): ImportedExtrasOffer {
  const held = new Set<string>();
  (library || []).forEach(definition => {
    const key = CustomMarker.keyFor(definition?.name || '');
    if (key) {
      held.add(key);
    }
  });

  const offer: ImportedExtrasOffer = {
    addable: [],
    nameable: [],
    fillable: [],
    natively: [],
  };
  const seen = new Set<string>();

  (extras || []).forEach(row => {
    // The NAME, not the key. `CustomMarker.keyFor` prefixes what it is given,
    // so it answers a truthy key for a blank name and cannot be used as the
    // emptiness test. Bloodwork's own hydration drops nameless rows too, but
    // this function is called with raw arrays in tests and from the lambda's
    // shape, so it checks for itself.
    if (!String(row?.name || '').trim()) {
      return;
    }
    const key = CustomMarker.keyFor(row.name);
    // A report that prints the same leftover twice is one job, not two.
    if (seen.has(key)) {
      return;
    }
    seen.add(key);

    if (held.has(key)) {
      offer.fillable.push(row);
      return;
    }
    const hit = catalogMarkerFor(row);
    if (hit) {
      // A catalog marker LabSmarts has since shipped, in an environment that
      // draws it, must not be added a second time. See `natively` above.
      if (hit.marker.shippedAs && isShippedMarkerCharted(hit.marker.shippedAs)) {
        offer.natively.push({ row, attribute: hit.marker.shippedAs });
      } else {
        offer.addable.push({ row, panel: hit.panel, marker: hit.marker });
      }
    } else {
      offer.nameable.push(row);
    }
  });

  return offer;
}

/** Whether a test has anything worth telling the practitioner about. */
export function hasImportedExtrasToOffer(offer: ImportedExtrasOffer): boolean {
  return (
    offer.fillable.length > 0 ||
    offer.addable.length > 0 ||
    offer.nameable.length > 0 ||
    // Nothing to press, but still worth saying: these are on the report and
    // not on the chart, and the practitioner is the only one who can fix it.
    offer.natively.length > 0
  );
}

/**
 * Whether one button can finish the job, with nothing left for the
 * practitioner to set up by hand.
 */
export function offerIsFullyAutomatic(offer: ImportedExtrasOffer): boolean {
  return (
    offer.nameable.length === 0 &&
    // A row that needs the report uploaded again is work left over too, so
    // the button must not claim to finish the job ("Add all 9").
    offer.natively.length === 0 &&
    (offer.fillable.length > 0 || offer.addable.length > 0)
  );
}

/**
 * The definitions to append to the library for everything addable.
 *
 * Built through the catalog's own per-panel grouping so each marker lands in
 * the section that panel puts it in, and so `markersFromPanel`'s idempotence
 * applies: a name already taken is skipped rather than added a second time,
 * which Save would refuse anyway.
 *
 * `takenNames` is every name on the page, not every name in the saved profile,
 * for the same reason the panel picker passes it that way: two clicks before a
 * save must not add the same marker twice.
 */
export function definitionsForOffer(
  offer: ImportedExtrasOffer,
  takenNames: string[],
  newId: () => string,
): CustomMarkerDefinition[] {
  const byPanel = new Map<LabPanel, LabPanelMarker[]>();
  offer.addable.forEach(({ panel, marker }) => {
    const markers = byPanel.get(panel) || [];
    markers.push(marker);
    byPanel.set(panel, markers);
  });

  const taken = [...(takenNames || [])];
  const definitions: CustomMarkerDefinition[] = [];

  byPanel.forEach((markers, panel) => {
    // Only the rows this delivery actually carried, never the whole panel: the
    // practitioner asked for the markers on their test, and adding the other
    // nine rows of a CBC because one of them was a leftover is not that.
    const subset: LabPanel = { ...panel, markers };
    const { added } = markersFromPanelSubset(subset, taken, newId);
    added.forEach(definition => {
      definitions.push(definition);
      taken.push(definition.name);
    });
  });

  return definitions;
}

/**
 * `markersFromPanel` with this module's section rule.
 *
 * Kept as a one-line wrapper rather than inlined so there is a single place
 * that decides a delivered leftover goes into the panel's own section. Passing
 * an empty section name is what makes `panelSectionName` fall back to the
 * panel's title, which is where the panel picker puts it too, so a marker added
 * from here and the same marker added from the picker land together.
 */
function markersFromPanelSubset(
  panel: LabPanel,
  takenNames: string[],
  newId: () => string,
): { added: CustomMarkerDefinition[]; alreadyHad: string[] } {
  return markersFromPanel(panel, panelSectionName(panel, ''), takenNames, newId);
}

/**
 * The stored rows, as plain name/value pairs.
 *
 * Exported for the notice's own tests and for anything that wants to show the
 * practitioner what is waiting without caring how it got there.
 */
export function rowsForFill(bloodwork: Bloodwork): { name: string; value: string }[] {
  return (bloodwork?.importedExtras || []).map(row => ({
    name: row.name,
    value: row.value,
  }));
}

const RANGE_KEYS = [
  'labMin',
  'labMax',
  'optimalMin',
  'optimalMax',
  'alarmMin',
  'alarmMax',
] as const;

/**
 * Turn delivered rows into this test's own markers, against a library.
 *
 * THIS MIRRORS `placeInMyMarkers` IN lambdas/labResults/ingest.js, and the two
 * have to agree. A value placed here, when the practitioner defines the marker
 * after the fact, must land exactly as it would have landed had the definition
 * existed when the lab delivered. If you change a rule in one, change it in the
 * other: aliases are tried before the printed name, the DEFINITION's name,
 * units and section win over the row's, and a numeric marker handed words is
 * left unplaced rather than forced into a NaN.
 *
 * It is not shared code because the two sides cannot import each other: the
 * lambda is CommonJS with its own bundled copy of the models, which is the same
 * reason `generated/` exists at all.
 *
 * The range bounds are not decoration. A custom marker with no labMin and no
 * labMax builds no range, and a marker with no range draws no bar and grades
 * nothing, so it sits on the test invisible. That is precisely the bug this
 * whole feature exists to close.
 */
export function placeRowsOntoTest(
  rows: ImportedExtra[],
  library: CustomMarkerDefinition[] | undefined,
): { placed: CustomMarker[]; unplaced: ImportedExtra[] } {
  const definitions = new Map<string, CustomMarkerDefinition>();
  (library || []).forEach(definition => {
    const name = (definition?.name || '').trim();
    if (!name) {
      return;
    }
    const key = CustomMarker.keyFor(name);
    // First wins, same as the library's own duplicate rule.
    if (!definitions.has(key)) {
      definitions.set(key, definition);
    }
  });

  const placed: CustomMarker[] = [];
  const unplaced: ImportedExtra[] = [];

  (rows || []).forEach(row => {
    const definition = [...(row.aliases || []), row.name]
      .map(name => definitions.get(CustomMarker.keyFor(name || '')))
      .find(Boolean);
    if (!definition) {
      unplaced.push(row);
      return;
    }

    const marker = new CustomMarker({ name: definition.name });
    if (definition.section) {
      marker.section = definition.section;
    }
    if ((definition as any).practitionerSection) {
      marker.practitionerSection = (definition as any).practitionerSection;
    }

    if ((definition.resultType as string) === 'text') {
      marker.resultType = definition.resultType;
      marker.textValue = String(row.value);
      placed.push(marker);
      return;
    }

    const numeric = Number(row.value);
    if (row.value === '' || !isFinite(numeric)) {
      // The lab printed words and this marker charts a number. Kept waiting
      // rather than charted wrong.
      unplaced.push(row);
      return;
    }

    marker.value = numeric;
    const units = definition.units || row.units;
    if (units) {
      marker.units = units;
    }
    marker.chartType = definition.chartType || marker.chartType;
    if (typeof definition.precision === 'number') {
      marker.precision = definition.precision;
    }

    // A definition NESTS its bounds under `ranges`; a marker saved from the
    // entry form carries them flat. Both shapes are read, so a library saved
    // either way still places.
    const ranges: any = (definition as any).ranges || definition;
    RANGE_KEYS.forEach(key => {
      const bound = Number(ranges[key]);
      if (ranges[key] !== undefined && ranges[key] !== null && isFinite(bound)) {
        (marker as any)[key] = bound;
      }
    });

    placed.push(marker);
  });

  return { placed, unplaced };
}
